/**
 * #473 Rotating Card Wall - 3D 旋转卡片墙效果
 * 
 * 功能特点：
 * - 3D 旋转卡片墙（环形布局）
 * - 深度感和透视效果
 * - 平滑的旋转动画
 * - 渐变背景 + 光晕效果
 * 
 * 技术要点：
 * - 使用 CSS 3D transforms（rotateY + translateZ）
 * - 透视投影（perspective）
 * - Spring 动画（平滑旋转）
 * - 确定性动画（避免随机抖动）
 * 
 * 应用场景：
 * - 产品展示
 * - 团队介绍
 * - 特性列表
 * - 轮播内容
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

// 卡片数据
const CARDS = [
  { title: 'AI Agent', icon: '🤖', desc: '自动化任务执行' },
  { title: 'Claude', icon: '🧠', desc: '智能对话助手' },
  { title: 'Remotion', icon: '🎬', desc: '视频编程框架' },
  { title: 'OpenClaw', icon: '🦞', desc: 'AI 助手平台' },
  { title: 'MiniMax', icon: '🎵', desc: '语音合成服务' },
  { title: 'GPT-5', icon: '⚡', desc: '下一代语言模型' },
];

// 配色方案
const COLORS = {
  background: '#0a0a0f',
  cardBg: 'rgba(20, 20, 30, 0.8)',
  cardBorder: 'rgba(255, 255, 255, 0.1)',
  accent: '#f59e0b',
  secondary: '#06b6d4',
  text: '#ffffff',
};

// 单个卡片组件
const Card: React.FC<{
  index: number;
  total: number;
  frame: number;
  fps: number;
  radius: number;
}> = ({ index, total, frame, fps, radius }) => {
  // 计算每张卡片的角度（360度平均分配）
  const anglePerCard = 360 / total;
  const angle = index * anglePerCard;
  
  // 旋转动画（慢速旋转）
  const rotationProgress = spring({
    frame,
    fps,
    config: { damping: 100, stiffness: 50, mass: 1 },
  });
  
  // 当前旋转角度（3圈旋转）
  const currentAngle = angle + rotationProgress * 360 * 3;
  
  // 3D 变换
  const transform = `
    rotateY(${currentAngle}deg)
    translateZ(${radius}px)
  `;
  
  // 计算透明度（正面最亮，背面较暗）
  const normalizedAngle = ((currentAngle % 360) + 360) % 360;
  const angleFromFront = Math.min(normalizedAngle, 360 - normalizedAngle);
  const opacity = interpolate(angleFromFront, [0, 180], [1, 0.3]);
  
  // 缩放（正面较大，背面较小）
  const scale = interpolate(angleFromFront, [0, 180], [1, 0.7]);
  
  const card = CARDS[index];
  
  return (
    <div
      style={{
        position: 'absolute',
        width: 280,
        height: 180,
        backgroundColor: COLORS.cardBg,
        border: `1px solid ${COLORS.cardBorder}`,
        borderRadius: 16,
        backdropFilter: 'blur(10px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        transform,
        opacity,
        scale,
        boxShadow: `
          0 8px 32px rgba(0, 0, 0, 0.4),
          0 0 20px rgba(245, 158, 11, 0.1),
          inset 0 1px 0 rgba(255, 255, 255, 0.1)
        `,
      }}
    >
      {/* Icon */}
      <div
        style={{
          fontSize: 48,
          filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))',
        }}
      >
        {card.icon}
      </div>
      
      {/* Title */}
      <div
        style={{
          fontSize: 26,
          fontWeight: 'bold',
          color: COLORS.text,
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
        }}
      >
        {card.title}
      </div>
      
      {/* Description */}
      <div
        style={{
          fontSize: 18,
          color: 'rgba(255, 255, 255, 0.7)',
          textAlign: 'center',
          padding: '0 20px',
        }}
      >
        {card.desc}
      </div>
      
      {/* 渐变装饰线 */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 20,
          right: 20,
          height: 2,
          background: `linear-gradient(90deg, transparent, ${COLORS.accent}, ${COLORS.secondary}, transparent)`,
          borderRadius: 2,
        }}
      />
    </div>
  );
};

// 主组件
export const RotatingCardWall: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const radius = 450; // 旋转半径
  const total = CARDS.length;
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        perspective: '1200px', // 3D 透视
        overflow: 'hidden',
      }}
    >
      {/* 背景光晕 */}
      <div
        style={{
          position: 'absolute',
          width: 800,
          height: 800,
          background: `radial-gradient(circle, ${COLORS.accent}15, transparent 70%)`,
          borderRadius: '50%',
          filter: 'blur(60px)',
          animation: 'pulse 4s ease-in-out infinite',
        }}
      />
      
      {/* 辅助光晕 */}
      <div
        style={{
          position: 'absolute',
          width: 600,
          height: 600,
          background: `radial-gradient(circle, ${COLORS.secondary}15, transparent 70%)`,
          borderRadius: '50%',
          filter: 'blur(50px)',
          top: '20%',
          right: '10%',
        }}
      />
      
      {/* 3D 卡片容器 */}
      <div
        style={{
          position: 'relative',
          width: 280,
          height: 180,
          transformStyle: 'preserve-3d', // 保持 3D 空间
        }}
      >
        {CARDS.map((_, index) => (
          <Card
            key={index}
            index={index}
            total={total}
            frame={frame}
            fps={fps}
            radius={radius}
          />
        ))}
      </div>
      
      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          fontSize: 50,
          fontWeight: 'bold',
          color: COLORS.text,
          textShadow: `
            0 4px 12px rgba(0, 0, 0, 0.6),
            0 0 40px ${COLORS.accent}40
          `,
          letterSpacing: 2,
        }}
      >
        2026 AI 技术栈
      </div>
      
      {/* 底部装饰 */}
      <div
        style={{
          position: 'absolute',
          bottom: 60,
          fontSize: 26,
          color: 'rgba(255, 255, 255, 0.5)',
          letterSpacing: 1,
        }}
      >
        Remotion #473 - Rotating Card Wall
      </div>
    </AbsoluteFill>
  );
};

// 注册配置
export const rotatingCardWallConfig = {
  id: 'RotatingCardWall',
  component: RotatingCardWall,
  durationInFrames: 180, // 6 秒（30fps）
  fps: 30,
  width: 1920,
  height: 1080,
};
